/**
 * Approach A — zero-flicker theme bootstrap snapshot.
 *
 * The theme (branding identity colours + Dashboard Theme structural vars +
 * resolved dark/light mode) is normally applied imperatively by
 * `applyBranding` / `applyDashboardTheme` / the ThemeProvider effect — i.e.
 * AFTER React mounts and an authenticated API round-trip resolves. That's
 * why a refresh paints the static `index.css` defaults first, then swaps
 * (the visible flicker).
 *
 * This module persists the RESOLVED DOM theme state to localStorage so the
 * tiny inline `<head>` bootstrap in `public/index.html` can re-apply it
 * synchronously, before first paint, on the next load. We snapshot the live
 * DOM (final CSS custom properties + `.dark` class + body data-attrs +
 * fonts) rather than the raw config, so the inline applicator needs zero
 * theme logic and can never drift from the React apply functions.
 *
 * Two snapshots exist:
 *   • THEME_SNAPSHOT_KEY  — the authenticated user's full theme. Cleared on
 *     logout. Bootstrap uses it only when an auth token is present.
 *   • LOGIN_SNAPSHOT_KEY  — the PUBLIC host/tenant login brand captured after
 *     `/branding/lookup`, including its effective public theme mode. Persists
 *     across logout (it's public + host-specific) so the /login + /signup
 *     pages paint their brand before first paint.
 */
export const THEME_SNAPSHOT_KEY = "analyzio_theme_snapshot";
export const LOGIN_SNAPSHOT_KEY = "analyzio_login_snapshot";

const FAVICON_LINK_ID = "analyzio-dynamic-favicon";

// Body-level structural attributes owned by Dashboard Theme (see
// applyDashboardTheme). Stable, small set — safe to enumerate.
const BODY_ATTRS = [
    "data-skin",
    "data-card-style",
    "data-table-style",
    "data-gradient-charts",
    "data-animations",
    "data-btn-caps",
];

// Pull the FIRST quoted family from a font-stack CSS var value, e.g.
// `"Poppins", "Instrument Sans", sans-serif` -> "Poppins".
function firstFamily(val) {
    if (!val) return null;
    const m = /"([^"]+)"/.exec(val);
    return m ? m[1] : null;
}

/**
 * Read the current resolved theme from the DOM and persist it.
 *
 * @param {object} [opts]
 * @param {string} [opts.key]        localStorage key (defaults to the
 *                                    authenticated theme snapshot).
 * @param {boolean} [opts.forceDark] override the captured dark flag (login
 *                                    snapshots force light so an authenticated
 *                                    dark session never leaks onto /login).
 */
export function captureThemeSnapshot(opts) {
    opts = opts || {};
    const key = opts.key || THEME_SNAPSHOT_KEY;
    try {
        if (typeof document === "undefined") return;
        const root = document.documentElement;
        const body = document.body;

        // Every inline custom property currently set on <html> — exactly what
        // applyBranding (--brand-*) and applyDashboardTheme (--dash-*/--chart-*
        // /--radius/--btn-radius/skin css) wrote. Reading them back guarantees
        // no drift with the apply functions.
        const rootVars = {};
        const st = root.style;
        for (let i = 0; i < st.length; i++) {
            const name = st[i];
            if (name && name.indexOf("--") === 0) {
                const val = st.getPropertyValue(name);
                if (val) rootVars[name] = val;
            }
        }

        const bodyAttrs = {};
        if (body) {
            for (const a of BODY_ATTRS) {
                const v = body.getAttribute(a);
                if (v != null) bodyAttrs[a] = v;
            }
        }

        const fonts = [];
        const seen = new Set();
        for (const fam of [
            firstFamily(st.getPropertyValue("--analyzio-font")),
            firstFamily(st.getPropertyValue("--brand-heading-font")),
        ]) {
            if (fam && !seen.has(fam)) {
                seen.add(fam);
                fonts.push(fam);
            }
        }

        const favEl = document.getElementById(FAVICON_LINK_ID);
        // Resolved painted background — lets the inline bootstrap paint <html>
        // the correct colour during the dev-mode FOUC (before CSS injects) and
        // hardens the first prod paint too.
        const pageBg = body ? getComputedStyle(body).backgroundColor : "";

        const snapshot = {
            v: 1,
            dark: typeof opts.forceDark === "boolean" ? opts.forceDark : root.classList.contains("dark"),
            rootVars,
            rootFontSize: root.style.fontSize || "",
            bodyFontFamily: body ? body.style.fontFamily || "" : "",
            bodyAttrs,
            fonts,
            pageBg,
            title: document.title || "",
            favicon: favEl ? favEl.getAttribute("href") || "" : "",
            logo: (typeof document !== "undefined" && document.documentElement.dataset.brandLogo) || "",
        };
        localStorage.setItem(key, JSON.stringify(snapshot));
    } catch (_e) {
        /* best-effort — snapshot is a progressive enhancement */
    }
}

/**
 * Capture the PUBLIC login/signup brand (host/tenant) and its resolved public
 * mode. Persists across logout so repeat /login loads paint the brand before
 * first paint.
 */
export function captureLoginSnapshot(opts = {}) {
    captureThemeSnapshot({ key: LOGIN_SNAPSHOT_KEY, ...opts });
}

/**
 * Remove the AUTHENTICATED theme snapshot. Called on logout (auth-context)
 * and on any anonymous mount (SettingsProvider) so a previous user's theme
 * never leaks. The public login snapshot is intentionally left intact.
 */
export function clearThemeSnapshot() {
    try {
        localStorage.removeItem(THEME_SNAPSHOT_KEY);
    } catch (_e) {
        /* noop */
    }
}

/**
 * Synchronously read a persisted snapshot (or null). Used by ThemeProvider to
 * seed its initial resolved dark/light mode, and by SettingsProvider to
 * repaint the cached login brand on anonymous mount.
 */
export function readThemeSnapshot(key = THEME_SNAPSHOT_KEY) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
}

export function readLoginSnapshot() {
    return readThemeSnapshot(LOGIN_SNAPSHOT_KEY);
}

/**
 * Apply a snapshot to the live DOM. Mirrors the inline <head> bootstrap so the
 * React side can repaint the cached brand synchronously (e.g. on the anonymous
 * mount, so /login never flashes to the platform defaults). Fonts are omitted
 * here — they're loaded by applyBranding/loadGoogleFont on the same page.
 */
export function applySnapshotToDom(s) {
    try {
        if (!s || typeof document === "undefined") return;
        const root = document.documentElement;
        if (s.dark) root.classList.add("dark");
        else root.classList.remove("dark");
        if (s.rootVars) {
            for (const k in s.rootVars) {
                if (Object.prototype.hasOwnProperty.call(s.rootVars, k)) {
                    root.style.setProperty(k, s.rootVars[k]);
                }
            }
        }
        if (s.rootFontSize) root.style.fontSize = s.rootFontSize;
        if (s.pageBg) root.style.backgroundColor = s.pageBg;
        if (typeof s.dark === "boolean") root.style.colorScheme = s.dark ? "dark" : "light";
        if (s.title) document.title = s.title;
        if (s.favicon) {
            let fav = document.getElementById(FAVICON_LINK_ID);
            if (!fav) {
                fav = document.createElement("link");
                fav.id = FAVICON_LINK_ID;
                fav.rel = "icon";
                document.head.appendChild(fav);
            }
            fav.setAttribute("href", s.favicon);
        }
        if (s.logo) {
            root.dataset.brandLogo = s.logo;
            const bl = document.getElementById("app-boot-logo");
            if (bl) { bl.src = s.logo; bl.style.display = ""; }
        }
        const body = document.body;
        if (body) {
            if (s.bodyFontFamily) body.style.fontFamily = s.bodyFontFamily;
            if (s.bodyAttrs) {
                for (const a in s.bodyAttrs) {
                    if (Object.prototype.hasOwnProperty.call(s.bodyAttrs, a)) {
                        body.setAttribute(a, s.bodyAttrs[a]);
                    }
                }
            }
        }
    } catch (_e) {
        /* best-effort */
    }
}
